<template>
  <figure class="viz-card">
    <figcaption class="viz-header">
      <h3>{{ title }}</h3>
      <div v-if="$slots.actions" class="viz-actions"><slot name="actions" /></div>
    </figcaption>

    <div v-if="loading" class="viz-empty">Loading…</div>
    <div v-else-if="total === 0" class="viz-empty">{{ emptyText }}</div>

    <!-- Always mounted (v-show, not v-if/v-else) - see BarChart.vue for why. -->
    <apexchart
      v-show="!loading && total > 0"
      type="donut"
      height="260"
      class="apex-clickable"
      :options="chartOptions"
      :series="chartSeries"
      @dataPointSelection="onSelect"
    />
  </figure>
</template>

<script>
import { toneColor, categoricalColor } from './chartPalette.js';

export default {
  name: 'StackedBar',
  props: {
    title: { type: String, required: true },
    // [{ label, value, tone: 'good' | 'warning' | 'critical' | 'neutral' | 'accent' }]
    segments: { type: Array, default: () => [] },
    emptyText: { type: String, default: 'No data available.' },
    loading: { type: Boolean, default: false },
    formatValue: { type: Function, default: null },
  },
  emits: ['segment-click'],
  computed: {
    total() {
      return this.segments.reduce((sum, s) => sum + (s.value || 0), 0);
    },
    visibleSegments() {
      const total = this.total;
      return this.segments
        .filter((s) => s.value > 0)
        .map((s) => ({ ...s, pct: total ? (s.value / total) * 100 : 0 }));
    },
    chartSeries() {
      return this.visibleSegments.map((s) => s.value);
    },
    chartOptions() {
      const format = this.format;
      return {
        chart: {
          fontFamily: 'system-ui, -apple-system, "Segoe UI", sans-serif',
          animations: { easing: 'easeout', speed: 300 },
        },
        labels: this.visibleSegments.map((s) => s.label),
        // A segment with an explicit `tone` keeps the fixed semantic color
        // (good/warning/critical/accent); one with no tone at all is a plain
        // category (e.g. task scope codes) and gets a cycled categorical
        // color instead, so a chart with many such segments doesn't collapse
        // them all onto the same default "neutral" grey.
        colors: this.visibleSegments.map((s, i) => (s.tone ? toneColor(s.tone) : categoricalColor(i))),
        stroke: { width: 2, colors: ['var(--surface-1)'] },
        dataLabels: {
          enabled: true,
          formatter: (val) => `${val.toFixed(0)}%`,
          style: { fontSize: '12px', fontWeight: 600 },
        },
        legend: {
          position: 'bottom',
          fontSize: '12.5px',
          markers: { size: 6 },
          itemMargin: { horizontal: 10, vertical: 4 },
        },
        tooltip: {
          y: { formatter: (val) => format(val) },
        },
        plotOptions: {
          pie: { donut: { size: '62%' }, expandOnClick: false },
        },
        states: {
          hover: { filter: { type: 'darken', value: 0.92 } },
          active: { filter: { type: 'darken', value: 0.85 } },
        },
      };
    },
  },
  methods: {
    format(value) {
      return this.formatValue ? this.formatValue(value) : value.toLocaleString();
    },
    onSelect(event, chartContext, config) {
      const segment = this.visibleSegments[config.dataPointIndex];
      if (segment) this.$emit('segment-click', segment);
    },
  },
};
</script>

<style scoped>
.viz-card {
  background: var(--surface-1);
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: 18px 20px 20px;
  margin: 0;
  flex: 1 1 320px;
  min-width: 0; /* let this shrink below its content's intrinsic width instead of forcing the row wider / wrapping a sibling out of view */
  box-shadow: 0 1px 2px rgba(11, 11, 11, 0.04);
  transition: box-shadow 0.15s ease;
}

.viz-card:hover {
  box-shadow: 0 6px 16px rgba(11, 11, 11, 0.07);
}

.viz-header {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid var(--gridline);
}

.viz-actions {
  margin-left: auto;
  /* negative margin keeps the header the same height as cards without controls */
  margin-top: -8px;
  margin-bottom: -8px;
  align-self: center;
}

.viz-header h3 {
  font-size: 14.5px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
}

.viz-empty {
  font-size: 13px;
  color: var(--text-muted);
  padding: 24px 0;
  text-align: center;
}

.apex-clickable :deep(.apexcharts-pie-area) {
  cursor: pointer;
}
</style>
